import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, MapPin, Calendar, Check, Flame } from "lucide-react";

const PILL = (c) => {
  if (c === "red" || c === "orange" || c === "amber") return { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" };
  if (c === "light_green" || c === "dark_green") return { bg: "bg-good-soft", text: "text-good", border: "border-good/40" };
  return { bg: "bg-secondary", text: "text-ink-muted", border: "border-ink-line" };
};
const BAR = (c) => (c === "red" || c === "orange" || c === "amber") ? "bg-warn" : (c === "light_green" || c === "dark_green") ? "bg-good" : "bg-brand";

export default function PceiDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: sch }] = await Promise.all([
          api.get(`/pcei/submissions/${id}`),
          api.get(`/pcei/schema`),
        ]);
        setData(d); setSchema(sch);
      } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message); }
    })();
  }, [id]);

  if (!data || !schema) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;
  const t = data.tender || {};
  const headlinePill = PILL(data.tier_color);

  return (
    <div className="space-y-6" data-testid="pcei-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to PCEI submissions
      </button>

      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">PCEI Assessment</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{t.client || "—"}</h1>
          <div className="text-base text-ink mt-1">{t.title || "—"}</div>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-ink-muted">
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {data.user_name || data.user_email || "—"}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t.location || "—"}</span>
            <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5" /> {data.completed_at ? new Date(data.completed_at).toLocaleString() : (data.updated_at ? new Date(data.updated_at).toLocaleString() : "—")}</span>
            {t.sr_no != null && <span className="font-num text-xs">SR #{String(t.sr_no).padStart(3, "0")}</span>}
            <span className="text-xs uppercase tracking-wider">{data.status}</span>
          </div>
        </div>
      </div>

      <div className={`card-flat p-7 border-l-4 ${headlinePill.border}`}>
        <div className="flex items-baseline gap-2">
          <div className="font-num text-7xl font-bold leading-none">{data.total_score}</div>
          <div className="text-2xl font-num text-ink-muted">/ 100</div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${headlinePill.bg} ${headlinePill.text} ${headlinePill.border}`}>{data.tier_label || "—"}</span>
        </div>
        {data.action && <p className="text-sm text-ink-muted mt-3 max-w-prose">{data.action}</p>}
        <div className="mt-3 text-xs text-ink-muted font-num">{data.answered} / {data.total_questions} answered</div>
      </div>

      <div className="card-flat p-6">
        <div className="stat-label">Category breakdown</div>
        <div className="space-y-3 mt-4">
          {(data.category_breakdown || []).map((c) => {
            const flagPill = PILL(c.flag_color);
            return (
              <div key={c.id} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted">{c.score} / {c.max_score}</span>
                    <span className={`chip text-[10px] ${flagPill.bg} ${flagPill.text} ${flagPill.border}`}>{c.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR(c.flag_color)}`} style={{ width: `${Math.min(100, c.pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {data.hotspots && data.hotspots.length > 0 && (
        <div className="card-flat p-6">
          <div className="flex items-center gap-2"><Flame className="w-4 h-4 text-warn" /><div className="stat-label">Risk Hotspots · {data.hotspots.length}</div></div>
          <div className="space-y-3 mt-4">
            {data.hotspots.map((h) => (
              <div key={h.code} className="p-4 rounded-sm border border-warn/30 bg-warn-soft/40">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display font-bold text-sm">{h.code} — {h.option}</div>
                  <div className="font-num text-sm font-bold text-warn">{h.score.toFixed(1)}</div>
                </div>
                <div className="text-xs text-ink-muted mt-1">{h.question}</div>
                {h.mitigation && <div className="text-xs text-ink mt-3 italic leading-relaxed">Mitigation: {h.mitigation}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-flat" data-testid="pcei-detail-answers">
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="stat-label">Question-by-question detail</div>
          <div className="font-display font-bold text-lg tracking-tight mt-1">All 40 answers</div>
        </div>
        <div className="divide-y divide-ink-line">
          {schema.questions.map((q) => {
            const selectedIdx = data.answers?.[q.code];
            const cat = schema.categories.find((c) => c.id === q.category);
            return (
              <div key={q.code} className="px-6 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-1">{cat?.label} · {q.code}</div>
                <div className="text-sm font-medium mb-3">{q.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((o, i) => {
                    const isSel = selectedIdx === i;
                    return (
                      <div key={i} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-sm border ${isSel ? "border-brand bg-brand/5 text-ink" : "border-ink-line bg-white text-ink-muted"}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${isSel ? "border-brand bg-brand text-white" : "border-ink-line"}`}>
                            {isSel && <Check className="w-3 h-3" />}
                          </span>
                          {o.label}
                        </span>
                        <span className={`font-num text-xs ${isSel ? "text-brand font-bold" : "text-ink-muted"}`}>{o.score.toFixed(1)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
